import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component({
  selector: 'app-otp-input',
  template: `
    <div class="prose-display-xs grid grid-cols-6 gap-x-2 font-semibold">
      <input
        *ngFor="let digit of digits.controls; let index = index"
        #input
        type="text"
        maxlength="1"
        class="rounded-none border-0 border-b border-neutral-200 px-3 py-2 text-center outline-none focus:border-primary-900 focus:ring-0"
        [formControl]="digit"
        app-number-only
        [allowDecimals]="false"
        [allowSign]="false"
        inputmode="numeric"
        (input)="handleInput($event, index)"
        (focus)="handleFocus($event)"
        (keydown)="handleKeyDown($event)"
        (paste)="handlePaste($event, index)" />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: OtpInputComponent,
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: OtpInputComponent,
      multi: true
    }
  ]
})
export class OtpInputComponent implements OnInit, ControlValueAccessor, Validator {
  onChange: Function = (value: string) => {};
  onTouched: Function = () => {};
  onValidatorChange: Function = () => {};

  digits!: FormArray<FormControl<string | null>>;

  @ViewChildren('input') inputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor() {}

  ngOnInit(): void {
    this.digits = new FormArray(
      Array(6)
        .fill(null)
        .map(x => new FormControl(''))
    );
  }

  ngAfterViewInit() {
    if (!this.inputs) return;
    this.inputs.first.nativeElement.focus();
  }

  writeValue(obj: any): void {
    this.digits.setValue(new Array(6).fill(''));
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.digits.disable();
    } else {
      this.digits.enable();
    }
  }
  validate(control: AbstractControl<string, string>): ValidationErrors | null {
    if (!control.value || control.value.length < 6) {
      return {
        otpInput: 'Value is incorrect'
      };
    }

    return null;
  }
  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  handleInput(event: Event, idx: number): void {
    const currentElement = event.target as HTMLInputElement;
    if (!(event instanceof InputEvent)) return;
    const nextFocusElement = <HTMLInputElement | null>(
      ((event as InputEvent).inputType === 'deleteContentBackward' ||
      (event as InputEvent).inputType === ''
        ? currentElement.previousElementSibling
        : currentElement.nextElementSibling)
    );
    if (nextFocusElement) nextFocusElement.focus();
    if (!nextFocusElement && idx == 5) {
      currentElement.select();
    }
    this.onChange(this.digits.value.join(''));
  }

  handleKeyDown(event: KeyboardEvent): void {
    const currentElement = event.target as HTMLInputElement;
    if (currentElement.value) return;
    if (event.code !== 'Backspace' && event.code !== 'Delete') return;
    const prevElement = currentElement.previousElementSibling;
    if (!prevElement || !(prevElement instanceof HTMLInputElement)) return;
    event.preventDefault();
    prevElement.focus();
  }

  handleFocus(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  handlePaste(event: ClipboardEvent, index: number) {
    (event.target as HTMLInputElement).value = '';
    event.preventDefault();

    if (index !== 0) {
      // If the target input is not the first one - ignore
      return;
    }

    const pasteData = event.clipboardData?.getData('text');

    if (!pasteData) return;

    const digits = pasteData.match(/\d/g);

    if (!digits?.length) return;

    for (let i = 0; i < digits.slice(0, 6).length; i++) {
      this.digits.controls[i].setValue(digits[i]);
    }

    this.inputs.get(digits.length === 6 ? 5 : digits.length)?.nativeElement.focus();
    this.onChange(this.digits.value.join(''));
    this.onTouched();
  }
}
